<script setup>
import { isEmptyObject, lreplace } from '@js/utils/toolbox';
import { Base64 } from '@js/plugins/base64';
import { ConnectApi } from '@js/plugins/connectApi';
import ResultsCard from '../ResultsCard/ResultsCard.vue';
import { l } from '@utils/toolbox';
import { resultsRender } from '@js/components/resultsRender';

const props = defineProps({
	responseId: {
		type: String,
		required: true
	},
	sorting: {
		type: String,
		required: false,
		default: 'best'
	},
	filters: {
		type: Object,
		required: false,
		default: () => ({})
	}
});

const isLoaded = ref(false);
const bestFlight = ref(null);
const totalResults = ref(0);
const activeFilters = ref(props.filters);

const hasFilters = computed(() => !isEmptyObject(activeFilters.value));
const isNoResults = computed(() => isLoaded.value && totalResults.value === 0);
const hasResults = computed(() => isLoaded.value && totalResults.value > 0 && bestFlight.value);

const shouldShowResultsLink = computed(() => (
	hasResults.value || (isNoResults.value && hasFilters.value)
));

const resultsLinkText = computed(() => {
	if (isNoResults.value && hasFilters.value) {
		return l('chat.flightResults.noFlightsMatchFilters');
	}

	return lreplace('chat.flightResults.showAllResults', { total: totalResults.value });
});

resultsRender.init();

async function fetchResult (filters = activeFilters.value) {
	if (!props.responseId) {
		return;
	}

	isLoaded.value = false;
	bestFlight.value = null;
	totalResults.value = 0;

	const result = await ConnectApi('Search', 'getResult', [{
		id: props.responseId,
		sorting: props.sorting,
		filters,
		prefix: true
	}]);

	const flight = result?.flights?.[0];

	totalResults.value = result?.meta?.resultCount || 0;
	bestFlight.value = flight || null;

	if (result && flight) {
		console.log('Merging flight search result into renderer:', { flight, result });
		resultsRender.mergeResponse(result, true, true);
	}

	isLoaded.value = true;
}

watch(() => props.responseId, async () => {
	activeFilters.value = props.filters;
	await fetchResult();
}, { immediate: true });


async function showMoreResults () {
	if (isNoResults.value && hasFilters.value) {
		activeFilters.value = {};
		await fetchResult({});
		return;
	}

	navigateTo('/resultat?id=' + props.responseId + filtersBase64Encoded());
}

function navigateTo (url) {
	window.open(url, '_blank');
}

function filtersBase64Encoded () {
	if (isEmptyObject(activeFilters.value)) {
		return '';
	}

	return `&filter=${Base64.encode(JSON.stringify(activeFilters.value))}`;
}
</script>

<template>
  <div class="flight-results-wrapper">
    <div
      v-if="!isNoResults"
      class="message-bubble assistant flight-card-bubble"
      :class="{ 'flight-card-bubble--loading': !isLoaded }"
    >
      <div class="flight-card-bubble__content">
        <ResultsCardSkeleton v-if="!isLoaded" />

        <ResultsCard
          v-if="bestFlight"
          :item="bestFlight"
          :filters="activeFilters"
        />
      </div>
    </div>

    <button
      v-if="shouldShowResultsLink"
      class="message-bubble assistant flight-options-disclosure"
      :class="{ 'flight-options-disclosure--empty': isNoResults }"
      type="button"
      @click="showMoreResults"
    >
      <span class="flight-options-left">
        <span class="flight-options-icon">ⓘ</span>
        <span>{{ resultsLinkText }}</span>
      </span>

      <span class="flight-options-arrow">›</span>
    </button>

    <div
      v-else-if="isNoResults"
      class="message-bubble assistant flight-no-results-compact"
    >
      <span class="flight-options-icon">ⓘ</span>
      <span>{{ l('chat.flightResults.noFlightsFound') }}</span>
    </div>
  </div>
</template>

<style lang="scss">
.flight-results-wrapper {
  display: flex;
  flex-direction: column;
  margin-top: 1.5rem;
  width: 100%;
  max-width: none;

  @include breakpoint_up(md) {
    max-width: 90%;
  }
}

.flight-card-bubble {
  padding: 0;
  width: 100%;
}

.flight-card-bubble--loading {
  min-height: 15rem;
}

.flight-card-bubble__content {
  width: 100%;
  height: 100%;
  padding: 0 1rem;
}


.flight-options-disclosure,
.flight-no-results-compact {
  width: fit-content;
  margin-top: 16px;
  color: $color-blue-800 !important;
  border: none !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.flight-no-results-compact {
  cursor: default;
  font-weight: 500;
}

.flight-options-disclosure {
  justify-content: space-between;
  min-width: 20rem;
}

.flight-options-disclosure--empty {
  margin-top: 0;
}

.flight-options-disclosure:hover {
  background: #f8fbff;
  border-color: #cfe0f4;
}

.flight-options-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.flight-options-icon {
  width: 2.5rem;
  height: 2.5rem;
  font-size: 2rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.flight-options-arrow {
  line-height: 1;
  display: flex;
  transform: translateY(-1px);
  height: 3.4rem;
  width: 3.4rem;
  padding-bottom: 0.7rem;
  font-size: 3rem;
  align-items: center;
  justify-content: space-around;
  background: $color-blue-900;
  color: $white;
  border-radius: 100%;
  margin: -1.5rem -0.5rem -1.5rem 0.5rem;
  flex: 0 0 auto;
}

@include breakpoint_down(md) {
  .flight-options-disclosure,
  .flight-no-results-compact {
    max-width: 100%;
  }

  .flight-options-disclosure {
    width: 100%;
    min-width: 0;
  }
}
</style>
